/**
 * The standard evaluation context skill generation prices against.
 *
 * **Generation only — not an appraisal default.** `BOOTSTRAP_CONTEXT` states
 * no tick length, no encounter motion and no orbit precision, and that
 * silence is not reinterpreted here. A caller that omits an `evalContext`
 * falls back to it, so a travelling shape defers first on the missing tick
 * (`ORBIT_EVALUATION_CONTEXT_UNAVAILABLE`); a caller that supplies a tick
 * but no motion defers on `ORBIT_ENCOUNTER_MOTION_UNKNOWN`. This context is a named,
 * explicit statement that the registered generation solve contexts
 * (`orchestration/structural-solve-context.ts`) attach on purpose.
 *
 * Every value is derived from an existing authority, never a new literal:
 *
 * - everything else: `BOOTSTRAP_CONTEXT`, unchanged, so no non-travelling
 *   shape prices differently (only a travelling stage reads the three
 *   fields below — see `EvalContext.encounterMotion`);
 * - `tickS`: the tick the ratified orbit precision profile is stated at,
 *   `ORBIT_PRECISION_REFERENCE_TICK_RATE_HZ`;
 * - `encounterMotion`: enemies drifting at the base enemy movement speed
 *   `BASE_ENEMY_SPEED` along +x past a caster standing still;
 * - `orbitPrecision`: `C1_BOOTSTRAP_TECHNICAL_PROFILE.orbit`, the resolution
 *   the runtime itself collides at.
 */
import { BOOTSTRAP_CONTEXT, type EvalContext } from '../context.js';
import { BASE_ENEMY_SPEED } from '../sim/archetypes.js';
import { C1_BOOTSTRAP_TECHNICAL_PROFILE, ORBIT_PRECISION_REFERENCE_TICK_RATE_HZ } from '../constraints/technical.js';

export const GENERATION_EVAL_CONTEXT_V1: EvalContext = Object.freeze({
  ...BOOTSTRAP_CONTEXT,
  tickS: 1 / ORBIT_PRECISION_REFERENCE_TICK_RATE_HZ,
  encounterMotion: Object.freeze({
    kind: 'CONSTANT_VELOCITY' as const,
    enemyVelocityMps: Object.freeze({ x: BASE_ENEMY_SPEED, y: 0 }),
    casterVelocityMps: Object.freeze({ x: 0, y: 0 }),
  }),
  orbitPrecision: Object.freeze({
    contactToleranceM: C1_BOOTSTRAP_TECHNICAL_PROFILE.orbit.contactToleranceM,
    maxSegmentsPerTick: C1_BOOTSTRAP_TECHNICAL_PROFILE.orbit.maxContactStepsPerInstancePerTick,
  }),
});
